/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import {
  ActionItem,
  FeedEvent,
  Lease,
  Process,
  Unit,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
} from '@/components/shared/Table';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { ExpiringLeaseItem } from './ExpiringLeaseItem';
import { getFeedEvents } from '@/data';
import {
  analyzeUnitProcesses,
  deriveRenewalCategory,
  renewalStatusLabel,
  type RenewalCategory,
} from './expiringLeasesUtils';
import { selectUnitStatusById } from '@/features/project/unitStatusSelectors';

type ExpiringLeasesTabContentProps = {
  units: Unit[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

export function ExpiringLeasesTabContent({
  units,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
}: ExpiringLeasesTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [expiresInDays, setExpiresInDays] = useState(90);
  const [showAffordable, setShowAffordable] = useState(true);
  const [showMarket, setShowMarket] = useState(true);
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [processCategories, setProcessCategories] = useState<string[]>([]);

  const dispatch = useDispatch();

  const processes: Process[] = useSelector((state: any) => state.project.processes);
  const allLeases: Lease[] = useSelector((state: any) => state.project.projectLeases) || [];
  const statusById = useSelector(selectUnitStatusById);

  // Build a map of current active lease per unit (valid, with the latest end date)
  const activeLeaseByUnitId = useMemo(() => {
    const map = new Map<string, Lease>();
    allLeases.forEach((lease: Lease) => {
      if (lease.status === 'valid') {
        const existing = map.get(lease.unitId);
        if (!existing || lease.leaseEndDate > existing.leaseEndDate) {
          map.set(lease.unitId, lease);
        }
      }
    });
    return map;
  }, [allLeases]);

  // Build a map of future leases per unit (valid lease starting after current lease ends)
  const futureLeaseByUnitId = useMemo(() => {
    const map = new Map<string, Lease>();
    allLeases.forEach((lease: Lease) => {
      if (lease.status === 'valid') {
        const currentLease = activeLeaseByUnitId.get(lease.unitId);
        if (currentLease && lease.id !== currentLease.id && lease.leaseStartDate > currentLease.leaseEndDate) {
          const existing = map.get(lease.unitId);
          if (!existing || lease.leaseStartDate < existing.leaseStartDate) {
            map.set(lease.unitId, lease);
          }
        }
      }
    });
    return map;
  }, [allLeases, activeLeaseByUnitId]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (units.length > 0) {
        const events = await getFeedEvents({ projectId: units[0].projectId });
        setFeedEvents(events);
      }
    };
    fetchEvents();
  }, [units]);

  const rows: ITableRow[] = useMemo(() => {
    if (feedEvents.length === 0) {
      return [];
    }

    // Filter units by their active lease's expiration date
    const filteredUnits = units.filter((unit) => {
      const activeLease = activeLeaseByUnitId.get(unit.id);
      if (!activeLease) return false;

      const endDate = new Date(activeLease.leaseEndDate);
      const today = new Date();
      const diff = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diff > expiresInDays || diff < 0) return false;

      if (unit.affordable && showAffordable) return true;
      if (!unit.affordable && showMarket) return true;
      return false;
    });

    const unitsData = filteredUnits.map((unit) => {
      const activeLease = activeLeaseByUnitId.get(unit.id)!;
      const endDate = new Date(activeLease.leaseEndDate);
      const days = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

      // Filter processes to only lease_renewal and move_out types
      const unitProcesses = processes.filter(
        (p) => p.unitId === unit.id && (p.type === 'lease_renewal' || p.type === 'move_out')
      );

      const displayProcess = analyzeUnitProcesses(unit, unitProcesses, feedEvents, actionItems);
      const completedProcess = displayProcess.find((p) => p.process.status === 'completed');

      // Bucket from processes *and* status events — see deriveRenewalCategory.
      const unitStatus = statusById[unit.id];
      const renewalCategory: RenewalCategory = deriveRenewalCategory(displayProcess, unitStatus);
      const statusLabel = renewalStatusLabel(displayProcess);

      const hasFutureLease = futureLeaseByUnitId.has(unit.id);
      const futureLease = futureLeaseByUnitId.get(unit.id);

      return {
        id: unit.id,
        expires: endDate,
        days,
        type: unit.affordable ? 'affordable' : 'market',
        rent: activeLease.monthlyRent ?? unit.actualRent ?? 0,
        sqft: unit.sqft ?? 0,
        unit,
        displayProcess,
        completedProcess,
        renewalCategory,
        renewalStatusLabel: statusLabel,
        unitStatus,
        hasFutureLease,
        futureLease,
      };
    });

    return unitsData
      .sort((a, b) => a.days - b.days)
      .map((unit) => {
        const completedProcess = unit.completedProcess;
        const processVisualLabel = unit.renewalCategory;

        const data = {
          Unit: {
            label: unit.id,
            value: unit.id,
            className: 'border-l border-r border-zinc-600',
          },
          Rent: {
            label: `$${getLongNumberStr(unit.rent || 0)}`,
            value: unit.rent,
            className: 'border-r border-zinc-600 justify-end',
          },
          Type: {
            label: unit.type,
            value: unit.type,
            className: 'border-r border-zinc-600',
          },
          Days: {
            label: `${unit.days}`,
            value: unit.days,
            className: 'border-r border-zinc-600 justify-end',
          },
          'Lease Expires': {
            label: unit.expires?.toLocaleDateString(),
            value: unit.expires,
            className: 'border-r border-zinc-600 justify-end px-2',
          },
          'Annual Rent/Sqft': {
            label: `$${(unit.rent / unit.sqft).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            value: unit.rent / unit.sqft,
            className: 'border-r border-zinc-600 justify-end',
          },
          Sqft: {
            label: `${unit.sqft}`,
            value: unit.sqft,
            className: 'border-r border-zinc-600 justify-end',
          },
          Process: {
            label: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.name : '',
            value: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.name : '',
          },
          'Process Outcome': {
            label: completedProcess ? completedProcess.outcome : '',
            value: completedProcess ? completedProcess.outcome : '',
          },
          'Process Status': {
            label: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.status : '',
            value: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.status : '',
          },
          'Process Visual': {
            label: processVisualLabel,
            value: processVisualLabel,
          },
          'Process Last Event': {
            label: unit.displayProcess.length > 0 ? unit.displayProcess[0].lastActionItem?.title : '',
            value: unit.displayProcess.length > 0 ? unit.displayProcess[0].lastActionItem?.title : '',
          },
          'Renewal Status': {
            label: unit.renewalStatusLabel,
            value: unit.renewalStatusLabel,
          },
          'Future Lease': {
            label: unit.hasFutureLease ? 'Yes' : '',
            value: unit.hasFutureLease ? 'Yes' : '',
          },
        } as Record<string, ITableCell>;

        return {
          id: unit.id,
          data,
          unit: unit.unit,
          hasFutureLease: unit.hasFutureLease,
          futureLease: unit.futureLease,
          unitStatus: unit.unitStatus,
        } as ITableRow;
      })
      .filter((row) => {
        if (processCategories.length === 0 || processCategories.length === 3) {
          return true;
        }
        return processCategories.includes(row.data['Process Visual'].label);
      });
  }, [actionItems, units, expiresInDays, showAffordable, showMarket, feedEvents, processes, processCategories, activeLeaseByUnitId, futureLeaseByUnitId, statusById]);

  const [selectedScale, setSelectedScale] = useState<ColorScaleOptions>('default');

  useEffect(() => {
    const data = rows.map((row) => {
      return {
        apartmentId: row.data['Unit'].value,
        value: row.data['Process Visual'].value,
        label: 'Type',
      };
    });

    let visualizer = new GenericDistinctVisualizer(
      new Map([
        ['no_process', colorScales[selectedScale][0]],
        ['renewing', colorScales[selectedScale][4]],
        ['moving_out', colorScales[selectedScale][1]],
      ])
    );
    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: visualizer,
      })
    );

    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  }, [rows, selectedScale]);

  const handleProceessCategoriesChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setProcessCategories(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const header = (
    <>
      <FormControlLabel
        sx={{ fontSize: '0.5rem' }}
        control={
          <Checkbox
            size="small"
            checked={showAffordable}
            onChange={(e) => setShowAffordable(e.target.checked as boolean)}
          />
        }
        label={<Typography variant="captionLight">Affordable</Typography>}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={showMarket}
            size="small"
            onChange={(e) => setShowMarket(e.target.checked as boolean)}
          />
        }
        label={<Typography variant="captionLight">Market</Typography>}
      />
      <FormControl size="small">
        <InputLabel id="expires-in-days-label">
          <Typography variant="captionLight">Expires In</Typography>
        </InputLabel>
        <Select
          size="small"
          id="expires-in-days"
          value={expiresInDays}
          label="Expires In"
          onChange={(e) => setExpiresInDays(e.target.value as number)}
        >
          <MenuItem value={30}>
            <Typography variant="captionLight">30 Days</Typography>
          </MenuItem>
          <MenuItem value={60}>
            <Typography variant="captionLight">60 Days</Typography>
          </MenuItem>
          <MenuItem value={90}>
            <Typography variant="captionLight">90 Days</Typography>
          </MenuItem>
          <MenuItem value={120}>
            <Typography variant="captionLight">120 Days</Typography>
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small">
        <InputLabel id="process-categories-label">
          <Typography variant="captionLight">Status</Typography>
        </InputLabel>
        <Select
          size="small"
          id="process-categories-select"
          value={processCategories}
          label="Status"
          onChange={handleProceessCategoriesChange}
          renderValue={(selected) => {
            if (selected.length === 3 || selected.length === 0) {
              return 'All';
            }
            const labelMap: Record<string, string> = {
              renewing: 'Renewing',
              moving_out: 'Moving Out',
              no_process: 'No Process',
            };
            return selected.map((s) => labelMap[s] || s).join(', ');
          }}
          multiple
        >
          <MenuItem value={'renewing'}>
            <Typography variant="captionLight">Renewing</Typography>
          </MenuItem>
          <MenuItem value={'moving_out'}>
            <Typography variant="captionLight">Moving Out</Typography>
          </MenuItem>
          <MenuItem value={'no_process'}>
            <Typography variant="captionLight">No Process</Typography>
          </MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel id="colors-scale-label">
          <Typography variant="captionLight">Color Scale</Typography>
        </InputLabel>
        <Select
          labelId="colors-scale-label"
          value={selectedScale}
          onChange={(e) => {
            setSelectedScale(e.target.value as ColorScaleOptions);
          }}
          label="Color Scale"
          size="small"
        >
          <MenuItem value="blues">
            <Typography variant="captionLight">Blues</Typography>
          </MenuItem>
          <MenuItem value="default">
            <Typography variant="captionLight">Green to Maroon</Typography>
          </MenuItem>
          <MenuItem value="purpleToYellow">
            <Typography variant="captionLight">Purple to Yellow</Typography>
          </MenuItem>
          <MenuItem value="tealToMagenta">
            <Typography variant="captionLight">Teal to Magenta</Typography>
          </MenuItem>
          <MenuItem value="earthTones">
            <Typography variant="captionLight">Earth Tones</Typography>
          </MenuItem>
          <MenuItem value="rainbow">
            <Typography variant="captionLight">Rainbow</Typography>
          </MenuItem>
          <MenuItem value="pastel">
            <Typography variant="captionLight">Pastel</Typography>
          </MenuItem>
        </Select>
      </FormControl>
    </>
  );

  return (
    <div className="unit-mix h-[calc(100vh-112px)] w-full flex flex-col px-12" ref={ref}>
      <div className="relative right-0 top-0 z-10 mb-4 flex flex-row gap-4">
        {header}
      </div>
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col gap-24">
          {rows.map((row) => {
            const hasFutureLease = (row as any).hasFutureLease;
            const futureLease = (row as any).futureLease as Lease | undefined;

            return (
              <ExpiringLeaseItem
                key={row.id}
                row={row}
                processes={processes}
                unitStatus={(row as any).unitStatus}
                hasDraftLease={hasFutureLease}
                onHoverUnit={() => {
                  dispatch(setHoveredUnitId(row.id));
                  onHoverUnit(row.id);
                }}
                onWorkflowEnd={(unitId) => {
                  dispatch(setSelectedUnit({ apartmentId: row.id }));
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
